"use client";

import { useEffect, useState } from "react";
import { TranslateBody } from "~/types/tools";

import { APIKeyInput } from "./_components/APIKeyInput";
import { CodeBlock } from "./_components/CodeBlock";
import { LanguageSelect } from "./_components/LanguageSelect";
import { ModelSelect } from "./_components/ModelSelect";
import { TextBlock } from "./_components/TextBlock";

export default function CodeTranslateClient() {
  const [inputLanguage, setInputLanguage] = useState<string>("JavaScript");
  const [outputLanguage, setOutputLanguage] = useState<string>("Python");
  const [inputCode, setInputCode] = useState<string>("");
  const [outputCode, setOutputCode] = useState<string>("");
  const [model, setModel] = useState<string>("gpt-3.5-turbo");
  const [loading, setLoading] = useState<boolean>(false);
  const [hasTranslated, setHasTranslated] = useState<boolean>(false);
  const [apiKey, setApiKey] = useState<string>("");

  const handleTranslate = async () => {
    const maxCodeLength = model === "gpt-3.5-turbo" ? 6000 : 12000;

    if (!apiKey) {
      alert("Please enter an API key.");
      return;
    }

    if (inputLanguage === outputLanguage) {
      alert("Please select different languages.");
      return;
    }

    if (!inputCode) {
      alert("Please enter some code.");
      return;
    }

    if (inputCode.length > maxCodeLength) {
      alert(
        `Please enter code less than ${maxCodeLength} characters. You are currently at ${inputCode.length} characters.`,
      );
      return;
    }

    setLoading(true);
    setOutputCode("");

    const controller = new AbortController();

    const body: TranslateBody = {
      inputLanguage,
      outputLanguage,
      inputCode,
      model,
      apiKey,
    };

    const response = await fetch("/api/code/translate", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      signal: controller.signal,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      setLoading(false);
      alert("Something went wrong.");
      return;
    }

    const data = response.body;

    if (!data) {
      setLoading(false);
      alert("Something went wrong.");
      return;
    }

    const reader = data.getReader();
    const decoder = new TextDecoder();
    let done = false;
    let code = "";

    while (!done) {
      const { value, done: doneReading } = await reader.read();
      done = doneReading;
      const chunkValue = decoder.decode(value);

      code += chunkValue;

      setOutputCode((prevCode) => prevCode + chunkValue);
    }

    setLoading(false);
    setHasTranslated(true);
    copyToClipboard(code);
  };

  const copyToClipboard = (text: string) => {
    const el = document.createElement("textarea");
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
  };

  const handleApiKeyChange = (value: string) => {
    setApiKey(value);

    localStorage.setItem("apiKey", value);
  };

  useEffect(() => {
    if (hasTranslated) {
      handleTranslate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outputLanguage]);

  useEffect(() => {
    const apiKey = localStorage.getItem("apiKey");

    if (apiKey) {
      setApiKey(apiKey);
    }
  }, []);

  return (
    <div className="flex justify-center overflow-x-hidden">
      <div className="relative flex min-h-[500px] w-full max-w-screen-3xl flex-col items-center p-4 md:p-10">
        <h1 className="text-center font-heading text-4xl font-bold">
          AI Code Translator
        </h1>
        <div className="mt-4 hidden w-2/3 text-muted-foreground md:block">
          Revolutionize your coding workflow with Content Flywheel&apos;s AI
          code translator. Our powerful tool converts between 45 programming
          languages, from JavaScript to Python and beyond. Need custom code?
          Simply describe your needs in plain English, and watch as our AI
          code generator brings your ideas to life. Whether you&apos;re a
          seasoned developer or just starting out, our AI-powered solution
          streamlines cross-language projects and accelerates coding tasks.
          Experience the future of programming with{" "}
          <a href="/">Content Flywheel</a> – where AI meets coding efficiency.
        </div>
        <div className="w-full mt-20 mb-40">
          <div className="w-full flex flex-col lg:flex-row justify-between mb-16">
            <div className="w-full lg:w-1/2 text-center text-sm flex justify-center items-center">
              <span className="text-xs mr-4">
                <a
                  href="https://platform.openai.com/api-keys"
                  target="_blank"
                  rel="noreferrer"
                >
                  API Key
                </a>
              </span>
              <APIKeyInput apiKey={apiKey} onChange={handleApiKeyChange} />
            </div>

            <div className="w-full lg:w-1/2 flex justify-center space-x-4 mt-6 lg:mt-0">
              <ModelSelect
                model={model}
                onChange={(value) => setModel(value)}
              />

              <button
                className="btn btn-primary text-white px-6 py-2"
                onClick={() => handleTranslate()}
                disabled={loading}
              >
                {loading ? "Translating..." : "Translate"}
              </button>
            </div>
          </div>

          <div className="mt-2 text-center text-xs">
            {loading
              ? "Translating..."
              : hasTranslated
                ? "Output copied to clipboard!"
                : 'Enter some code and click "Translate"'}
          </div>

          <div className="mt-6 flex w-full flex-col justify-between items-start sm:flex-row sm:space-x-4 mx-auto">
            <div className="h-100 flex flex-col justify-center space-y-2 w-full lg:w-1/2">
              <div className="text-center text-xl font-bold">Input</div>
              <LanguageSelect
                language={inputLanguage}
                onChange={(value) => {
                  setInputLanguage(value);
                  setHasTranslated(false);
                  setInputCode("");
                  setOutputCode("");
                }}
              />

              {inputLanguage === "Natural Language" ? (
                <TextBlock
                  text={inputCode}
                  editable={!loading}
                  onChange={(value) => {
                    setInputCode(value);
                    setHasTranslated(false);
                  }}
                />
              ) : (
                <CodeBlock
                  code={inputCode}
                  editable={!loading}
                  onChange={(value) => {
                    setInputCode(value);
                    setHasTranslated(false);
                  }}
                />
              )}
            </div>
            <div className="mt-8 flex h-full flex-col justify-center space-y-2 sm:mt-0 w-full lg:w-1/2">
              <div className="text-center text-xl font-bold">Output</div>

              <LanguageSelect
                language={outputLanguage}
                onChange={(value) => {
                  setOutputLanguage(value);
                  setOutputCode("");
                }}
              />

              {outputLanguage === "Natural Language" ? (
                <TextBlock text={outputCode} />
              ) : (
                <CodeBlock code={outputCode} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
